/*
 * PRD-037 — Estado del comercio del borrador SIN Redux (app SEPARADA del partner).
 *
 * Espeja la superficie util de `useCommerce` del panel (`commerce` + `handleUpdateValue` +
 * `save`) pero sin el store de Redux: la app del partner no monta el Provider del panel. Carga y
 * persiste el borrador via `useFetchCallback` (GET/PUT `/v1/comercio`), que el interceptor de
 * axiosInstance autoriza con el JWT-draft que `useDraftSession` deja en localStorage.
 *
 * Debe montarse SOLO cuando la sesion-draft ya esta lista (token en localStorage); de lo contrario
 * el GET saldria sin scope al borrador.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Commerce } from '@interfaces/general';
import { useFetchCallback } from '@services/services';

interface DraftCommerceState {
  commerce: Commerce | null;
  loading: boolean;
  saving: boolean;
  loadError: boolean;
  // La sesion-draft (token en localStorage) expiro: un GET/PUT devolvio 401. Se distingue de
  // `loadError` (fallo generico) para que la UI muestre "sesion expirada" en vez del mensaje ambiguo.
  sessionExpired: boolean;
  handleUpdateValue: <K extends keyof Commerce>(key: K, value: Commerce[K]) => void;
  // Nunca falla en silencio: en error de `putCommerce()` (o si no hay borrador cargado) SIEMPRE
  // lanza (rechaza la promesa) — no hay un "retorno false" que el caller deba checar. El caller
  // (ver `persist()` en ConfigureCommerce.tsx) debe envolver en try/catch y re-lanzar/bloquear el
  // avance del wizard en el catch.
  save: () => Promise<void>;
}

export const useDraftCommerce = (): DraftCommerceState => {
  const [commerce, setCommerce] = useState<Commerce | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Handler de 401 propio: la app del partner NO monta el AuthProvider del panel, asi que el
  // `auth.logout()` interno de useFetchCallback es un no-op (no limpia el token-draft expirado →
  // toda request siguiente reusa el token invalido y falla en bucle). Aqui limpiamos las llaves del
  // draft (mismas que gestiona useDraftSession) y marcamos la sesion como expirada para la UI.
  const onUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('id_commerce');
    setSessionExpired(true);
  }, []);

  const { call: fetchCommerce } = useFetchCallback<Commerce>('get', '/v1/comercio', 'v1', { onUnauthorized });
  const { call: putCommerce, isFetching: saving } = useFetchCallback<Commerce, Commerce>('put', '/v1/comercio', 'v1', {
    onUnauthorized,
  });

  // Espejo del commerce para que `save` lea siempre el ultimo valor sin re-crear el callback.
  const commerceRef = useRef<Commerce | null>(null);
  useEffect(() => {
    commerceRef.current = commerce;
  }, [commerce]);

  useEffect(() => {
    let active = true;
    fetchCommerce()
      .then((data) => {
        if (!active) return;
        setCommerce(data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setLoadError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [fetchCommerce]);

  const handleUpdateValue = useCallback(<K extends keyof Commerce>(key: K, value: Commerce[K]) => {
    setCommerce((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  // Siempre lanza en error (no hay "retorno false" silencioso): ni si `putCommerce()` falla ni si
  // no hay borrador cargado todavia (no deberia pasar — los pasos del wizard solo se renderizan
  // con `commerce` ya cargado — pero si pasara, tambien debe bloquear el avance, no fallar en
  // silencio).
  const save = useCallback(async (): Promise<void> => {
    const current = commerceRef.current;
    if (!current) {
      throw new Error('useDraftCommerce.save(): no hay borrador cargado para guardar');
    }
    await putCommerce({ data: current });
  }, [putCommerce]);

  return { commerce, loading, saving, loadError, sessionExpired, handleUpdateValue, save };
};
